/**
 * Line up two text files against each other, and say where they differ.
 *
 * Behind SirCAD's Edit ▸ Align and Edit ▸ Compare, which are an adaptation of
 * the original's pair rather than a port of them.
 *
 * Its two are built for two windows open side by side. `filecomp`
 * (OGtxt9.cpp:69) walks both files from their current lines while they match,
 * stops at the first difference, searches outward for where the two
 * resynchronise, and selects that run in both windows — so running it again and
 * again walks you through the differences one at a time. `filealign`
 * (OGtxt9.cpp:216) does no comparing whatever: it scrolls both windows so each
 * file's current line sits at the same height, and redraws.
 *
 * Neither survives the move to tabs, where one file is on screen at a time and
 * there is no second window to level or to select in. So the pair is rebuilt as
 * one whole-file comparison shown two ways — the same question they answer
 * between them, asked once rather than a step at a time.
 *
 * The reason they are worth having in a PCB program at all is that half the
 * files a board produces are text — a netlist, a bill of materials, a drill
 * program, a pick-and-place — and the question you ask of two of them is always
 * "what changed". Diffing a netlist against yesterday's is how you find the one
 * connection that moved.
 *
 * Pure: no DOM, no Node. The caller decides how to paint it.
 */

/** What happened to one line of the aligned pair. */
export type LineKind = 'same' | 'changed' | 'added' | 'removed';

/**
 * One row of the alignment.
 *
 * Both sides are present for a matched row and one is null where the other file
 * has nothing to line up against — which is what makes the two columns sit
 * level when they are shown side by side.
 */
export interface AlignedRow {
  kind: LineKind;
  /** Line number in the left file, 1-based, or null where it has no line. */
  leftNo: number | null;
  rightNo: number | null;
  left: string | null;
  right: string | null;
}

export interface AlignResult {
  rows: AlignedRow[];
  /** Rows that are the same on both sides. */
  same: number;
  added: number;
  removed: number;
  changed: number;
}

/**
 * Split into lines without inventing one.
 *
 * `''.split(…)` gives `['']` — one empty line where the file has none — so an
 * empty file would align as a single blank against the other's first line and
 * report a change nobody made. And a file ending in a newline is not a file
 * with a blank last line, which is the other end of the same mistake.
 */
function lines(text: string): string[] {
  if (text === '') return [];
  const out = text.split(/\r\n|\r|\n/);
  if (out.length > 1 && out[out.length - 1] === '') out.pop();
  return out;
}

/**
 * Longest common subsequence of two line arrays, as a table of lengths.
 *
 * The classic dynamic-programming diff. Quadratic in the number of lines, which
 * is the reason for the guard in {@link alignFiles}: a netlist is a few thousand
 * lines and costs nothing, and two ten-thousand-line files would cost a hundred
 * million cells and lock the tab.
 *
 * Rows are computed one at a time but all kept, because walking back through
 * them is what produces the alignment — the space-saving variants give the
 * length and not the path.
 */
function lcsTable(a: string[], b: string[]): Uint32Array[] {
  const table: Uint32Array[] = [];
  for (let i = 0; i <= a.length; i++) table.push(new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    const row = table[i]!;
    const next = table[i + 1]!;
    for (let j = b.length - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? next[j + 1]! + 1 : Math.max(next[j]!, row[j + 1]!);
    }
  }
  return table;
}

/** How many lines the pair may total before the diff is refused. */
export const ALIGN_LIMIT = 20_000;

/**
 * Align two files line by line.
 *
 * A removal immediately followed by an addition is reported as one changed row
 * rather than as two, so an edited line sits level with what it replaced. That
 * is the whole point of Align over Compare: you want to read the two versions
 * of the line against each other, not hunt for the partner of every deletion.
 */
export function alignFiles(leftText: string, rightText: string): AlignResult {
  const a = lines(leftText);
  const b = lines(rightText);

  if (a.length + b.length > ALIGN_LIMIT) {
    throw new Error(
      `Too much to align — ${a.length + b.length} lines against a limit of `
      + `${ALIGN_LIMIT}. Comparing two files this size line by line would take `
      + 'longer than reading them.');
  }

  const table = lcsTable(a, b);
  const rows: AlignedRow[] = [];

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      rows.push({ kind: 'same', leftNo: i + 1, rightNo: j + 1, left: a[i]!, right: b[j]! });
      i++; j++;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      rows.push({ kind: 'removed', leftNo: i + 1, rightNo: null, left: a[i]!, right: null });
      i++;
    } else {
      rows.push({ kind: 'added', leftNo: null, rightNo: j + 1, left: null, right: b[j]! });
      j++;
    }
  }
  while (i < a.length) {
    rows.push({ kind: 'removed', leftNo: i + 1, rightNo: null, left: a[i]!, right: null });
    i++;
  }
  while (j < b.length) {
    rows.push({ kind: 'added', leftNo: null, rightNo: j + 1, left: null, right: b[j]! });
    j++;
  }

  return summarise(pairUp(rows));
}

/**
 * Fold each removal-then-addition into one changed row.
 *
 * Run for run, not line for line: three lines replaced by three is three
 * changed rows, and three replaced by five is three changed and two added. The
 * leftovers keep their own kind so nothing is lost or invented.
 */
function pairUp(rows: AlignedRow[]): AlignedRow[] {
  const out: AlignedRow[] = [];
  let i = 0;
  while (i < rows.length) {
    if (rows[i]!.kind !== 'removed') { out.push(rows[i]!); i++; continue; }

    let r = i;
    while (r < rows.length && rows[r]!.kind === 'removed') r++;
    let d = r;
    while (d < rows.length && rows[d]!.kind === 'added') d++;

    const removed = rows.slice(i, r);
    const added = rows.slice(r, d);
    const both = Math.min(removed.length, added.length);
    for (let k = 0; k < both; k++) {
      out.push({
        kind: 'changed',
        leftNo: removed[k]!.leftNo,
        rightNo: added[k]!.rightNo,
        left: removed[k]!.left,
        right: added[k]!.right,
      });
    }
    out.push(...removed.slice(both), ...added.slice(both));
    i = d;
  }
  return out;
}

function summarise(rows: AlignedRow[]): AlignResult {
  let same = 0; let added = 0; let removed = 0; let changed = 0;
  for (const r of rows) {
    if (r.kind === 'same') same++;
    else if (r.kind === 'added') added++;
    else if (r.kind === 'removed') removed++;
    else changed++;
  }
  return { rows, same, added, removed, changed };
}

/** True when the two files have nothing between them. */
export function identical(result: AlignResult): boolean {
  return result.added === 0 && result.removed === 0 && result.changed === 0;
}
